import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

/**
 * IDs of action tiles that are only visible to Sustain Team Members and Super Users.
 * Non-sustain/non-super users see Submit Ticket + My Tickets only.
 */
export const SUSTAIN_ONLY_ACTION_IDS: readonly string[] = [
  "sustain-queue",
  "notifications-comms",
  "team-management",
  "spare-pool",
] as const;

/**
 * Returns the current user's Ticketing Portal access level derived from
 * the `location-with-access` API response already stored in Redux.
 *
 * Access Levels:
 * - Standard User: Cannot see sustain features
 * - Sustain Team Member: Full read/write access to sustain features
 * - Super User (Non-Sustain): Read-only access to sustain features
 * - COE Role: Read-only access to reporting and analytics
 *
 * Return Values:
 * - `isSustainTeamMember` – true when `userInfo.sustainTeamMember === true`
 * - `isSuperUser` – true when `userInfo.roleName === "Super User"`
 * - `isCoeRole` – true when `userInfo.roleName === "COE"` or contains "center of excellence"
 * - `canViewSustainFeatures` – true if user can VIEW sustain features (Sustain OR Super User)
 * - `hasWriteAccess` – true if user can EDIT/WRITE sustain features (Sustain ONLY)
 * - `isReadOnly` – true if user has read-only access to sustain features (Super User without Sustain)
 * - `canViewReportingAnalytics` – true if user can view Reporting and Analytics (Sustain OR Super User OR COE)
 * - `isReportingAnalyticsReadOnly` – true if user has read-only access to analytics (Super User or COE without Sustain)
 * - `accessLoading` – true while the initial app-data API is in flight
 */
const useTicketingAccess = () => {
  const userInfo = useSelector((state: RootState) => state.appData.userInfo);
  const accessLoading = useSelector((state: RootState) => state.appData.loading);
  const normalizedRoleName = (userInfo?.roleName ?? "").trim().toLowerCase();

  const isSustainTeamMember = useMemo(
    () => userInfo?.sustainTeamMember === true,
    [userInfo]
  );

  const isSuperUser = useMemo(
    () => userInfo?.roleName === "Super User",
    [userInfo]
  );

  const isCoeRole = useMemo(
    () => normalizedRoleName === "coe" || normalizedRoleName.includes("center of excellence"),
    [normalizedRoleName]
  );

  // Super Users can VIEW sustain features even if not sustain team members
  const canViewSustainFeatures = useMemo(
    () => isSustainTeamMember || isSuperUser,
    [isSustainTeamMember, isSuperUser]
  );

  // Only actual sustain team members can write/edit
  const hasWriteAccess = useMemo(
    () => isSustainTeamMember,
    [isSustainTeamMember]
  );

  // Super Users who are not sustain members = read-only access
  const isReadOnly = useMemo(
    () => isSuperUser && !isSustainTeamMember,
    [isSuperUser, isSustainTeamMember]
  );

  const canViewReportingAnalytics = useMemo(
    () => isSustainTeamMember || isCoeRole || isSuperUser,
    [isSustainTeamMember, isCoeRole, isSuperUser]
  );

  const isReportingAnalyticsReadOnly = useMemo(
    () => (isCoeRole || isSuperUser) && !isSustainTeamMember,
    [isCoeRole, isSuperUser, isSustainTeamMember]
  );

  return {
    isSustainTeamMember,
    isSuperUser,
    isCoeRole,
    canViewSustainFeatures,
    hasWriteAccess,
    isReadOnly,
    canViewReportingAnalytics,
    isReportingAnalyticsReadOnly,
    accessLoading,
  };
};

export default useTicketingAccess;
